import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import MainLayout from '../components/MainLayout';
import { Api } from '../api/api';
import usePageTitle from '../utils/usePageTitle';
import { useToast } from '../components/ToastContainer';
import { useGlobalLoading } from '../hooks/useGlobalLoading';

// Определение типов данных
interface SneakerDetails {
  sneaker: {
    id: number;
    uk_size: number;
    us_size: number;
    eu_size: number;
    brand: {
      brand: string;
    };
    model: string;
    color: string;
    price: number;
    article: string;
    condition: {
      condition: string;
    };
    city: string;
    place: string;
    fitting: boolean;
    vendor: {
      first_name: string;
      last_name: string;
      vk_account: string;
      telegram_account: string;
      show_vk: boolean | null;
      show_telegram: boolean | null;
    };
    purchase_price: number;
    purchase_date: string;
    in_store: boolean;
    is_sale: boolean;
    sale_date: string | null;
  };
  photo_url: string;
  prices: {
    goat: number;
    poizon: number;
    stock_x: number;
    avg_price: number;
  };
}

// Стилизованные компоненты
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #609F94;
  margin-bottom: 24px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  padding: 0;
`;

const Content = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  width: 350px;
  height: 350px;
  border-radius: 16px;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1/1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: transparent;
`;

const DetailsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 28px;
  color: #FFFFFF;
  margin: 0;
`;

const Subtitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #609F94;
  margin: 0;
`;

const PriceInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const Price = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #FFFFFF; 
`;

const PurchasePrice = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #FFFFFF;
`;

const PriceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 24px;
  margin-top: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
`;

const DetailValue = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
`;

const VendorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: #172A2A;
  border-radius: 16px;
  margin-top: 24px;
`;

const VendorTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
  margin: 0;
`;

const VendorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VendorRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const VendorContact = styled.a`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #24BA7D;
  text-decoration: none;
`;

const MarketComparisonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 32px;
`;

const MarketComparisonTitle = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: #FFFFFF;
  margin: 0;
`;

const MarketGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const MarketCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #172A2A;
  border-radius: 16px;
  padding: 16px;
`;

const MarketHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MarketName = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
`;

const MarketPrice = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #24BA7D;
`;

const BackArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.8332 10H4.1665" stroke="#609F94" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.99984 15.8334L4.1665 10L9.99984 4.16669" stroke="#609F94" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SneakerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<SneakerDetails | null>(null);
  const { showError } = useToast();
  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();

  // Определяем, с какой страницы пришел пользователь
  const fromPage = (location.state as { from?: string })?.from || 'inventory';
  const activePage = fromPage === 'sales' ? 'sales' : 'inventory';

  // Динамический заголовок на основе данных о товаре
  usePageTitle(
    data
      ? `${data.sneaker.brand.brand} ${data.sneaker.model}`
      : 'Подробности товара'
  );

  // Управляем глобальной загрузкой
  useEffect(() => {
    if (isLoading) {
      addLoadingStep('sneaker-details-loading', 'Ищем лучшие цены...', 5);
    } else {
      removeLoadingStep('sneaker-details-loading');
    }
  }, [isLoading, addLoadingStep, removeLoadingStep]);

  useEffect(() => {
    const fetchSneakerDetails = async () => {
      if (!id) return;

      try {
        setIsLoading(true);

        const api = new Api();
        const response = await api.getSneakerById(parseInt(id));

        setData(response);
      } catch (err) {
        showError(err instanceof Error ? err.message : 'Ошибка при загрузке данных о кроссовках');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSneakerDetails();
  }, [id, showError]);

  const handleBack = () => {
    const backPath = fromPage === 'sales' ? '/sales' : '/inventory';
    navigate(backPath);
  };

  return (
    <MainLayout activePage={activePage}>
      <Container>
        <BackButton onClick={handleBack}>
          <BackArrowIcon />
          Назад
        </BackButton>

        {!isLoading && data ? (
          <>
            <Content>
              <ImageContainer>
                <Image src={data.photo_url || 'https://placehold.co/350'} alt={data.sneaker.model} />
              </ImageContainer>

              <DetailsContainer>
                <TitleSection>
                  <Title>{data.sneaker.brand.brand} {data.sneaker.model}</Title>
                  <Subtitle>{data.sneaker.color}</Subtitle>
                </TitleSection>

                <PriceInfoContainer>
                  <PriceSection>
                    <PriceItem>
                      <PriceLabel>Цена продажи:</PriceLabel>
                      <Price>{data.sneaker.price.toLocaleString()} ₽</Price>
                    </PriceItem>
                    {data.sneaker.purchase_price > 0 && (
                      <PriceItem>
                        <PriceLabel>Цена закупки:</PriceLabel>
                        <PurchasePrice>{data.sneaker.purchase_price.toLocaleString()} ₽</PurchasePrice>
                      </PriceItem>
                    )}
                  </PriceSection>

                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Артикул</DetailLabel>
                      <DetailValue>{data.sneaker.article}</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Размеры</DetailLabel>
                      <DetailValue>
                        US: {data.sneaker.us_size} | UK: {data.sneaker.uk_size} | EU: {data.sneaker.eu_size}
                      </DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Состояние</DetailLabel>
                      <DetailValue>{data.sneaker.condition.condition}</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Статус</DetailLabel>
                      <DetailValue>
                        {data.sneaker.is_sale ? 'Продано' : (data.sneaker.in_store ? 'В наличии' : 'Скрыто')}
                      </DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Город</DetailLabel>
                      <DetailValue>{data.sneaker.city}</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Место</DetailLabel>
                      <DetailValue>{data.sneaker.place}</DetailValue>
                    </DetailItem>

                    <DetailItem>
                      <DetailLabel>Примерка</DetailLabel>
                      <DetailValue>{data.sneaker.fitting ? 'Доступна' : 'Не доступна'}</DetailValue>
                    </DetailItem>

                    {data.sneaker.sale_date && (
                      <DetailItem>
                        <DetailLabel>Дата продажи</DetailLabel>
                        <DetailValue>{new Date(data.sneaker.sale_date).toLocaleDateString()}</DetailValue>
                      </DetailItem>
                    )}

                    {data.sneaker.purchase_date && data.sneaker.purchase_date !== '0001-01-01' && (
                      <DetailItem>
                        <DetailLabel>Дата покупки</DetailLabel>
                        <DetailValue>{new Date(data.sneaker.purchase_date).toLocaleDateString()}</DetailValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </PriceInfoContainer>

                <VendorSection>
                  <VendorTitle>Продавец</VendorTitle>
                  <VendorInfo>
                    <DetailValue>{data.sneaker.vendor.first_name} {data.sneaker.vendor.last_name}</DetailValue>

                    {data.sneaker.vendor.show_telegram && data.sneaker.vendor.telegram_account && (
                      <VendorRow>
                        <DetailLabel>Telegram:</DetailLabel>
                        <VendorContact
                          href={`https://t.me/${data.sneaker.vendor.telegram_account.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data.sneaker.vendor.telegram_account}
                        </VendorContact>
                      </VendorRow>
                    )}

                    {data.sneaker.vendor.show_vk && data.sneaker.vendor.vk_account && (
                      <VendorRow>
                        <DetailLabel>VK:</DetailLabel>
                        <VendorContact
                          href={`https://vk.com/${data.sneaker.vendor.vk_account.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data.sneaker.vendor.vk_account}
                        </VendorContact>
                      </VendorRow>
                    )}
                  </VendorInfo>
                </VendorSection>

                <MarketComparisonSection>
                  <MarketComparisonTitle>Сравнение цен</MarketComparisonTitle>
                  <MarketGrid>
                    <MarketCard>
                      <MarketHeader>
                        <MarketName>StockX</MarketName>
                      </MarketHeader>
                      <MarketPrice>
                        {data.prices.stock_x > 0 ? `${data.prices.stock_x.toLocaleString()} ₽` : 'Нет данных'}
                      </MarketPrice>
                    </MarketCard>

                    <MarketCard>
                      <MarketHeader>
                        <MarketName>GOAT</MarketName>
                      </MarketHeader>
                      <MarketPrice>
                        {data.prices.goat > 0 ? `${data.prices.goat.toLocaleString()} ₽` : 'Нет данных'}
                      </MarketPrice>
                    </MarketCard>

                    <MarketCard>
                      <MarketHeader>
                        <MarketName>Poizon</MarketName>
                      </MarketHeader>
                      <MarketPrice>
                        {data.prices.poizon > 0 ? `${data.prices.poizon.toLocaleString()} ₽` : 'Нет данных'}
                      </MarketPrice>
                    </MarketCard>

                    <MarketCard>
                      <MarketName>Средняя цена</MarketName>
                      <MarketPrice>
                        {data.prices.avg_price > 0 ? `${data.prices.avg_price.toLocaleString()} ₽` : 'Нет данных'}
                      </MarketPrice>
                    </MarketCard>
                  </MarketGrid>
                </MarketComparisonSection>
              </DetailsContainer>
            </Content>
          </>
        ) : null}
      </Container>
    </MainLayout>
  );
};

export default SneakerDetails;
